import * as React from 'react';
import { useNavigate } from 'react-router';
import { IFeatureFlags, ITenantConfig } from 'data/tenant-config';
import { sendApiRequest } from 'utils/network';
import { classNames } from 'utils/ui/css';
import { ITenant } from './types';
import { NewMarketForm } from './NewMarketForm';
import { NewTenantForm } from './NewTenantForm';
import { user } from 'utils/auth/user';
import { CustomerFacingError, reportUntoastedApiFailure } from 'utils/ui/errors';
import logger from 'utils/log';
import { EErrorCode } from 'config/errors-codes';
import ConfiguratorValidationDialog from 'components/ConfiguratorValidationDialog';
import { getMarketDisplayName } from './marketFlags';
import { ContextMenu } from './ContextMenu';
import { brandLogos } from './brandLogos';
import Dialog from './Dialog';
import { createMarketFilter } from 'utils/selfservice/marketSearch';
import { encodeTenantParam } from 'utils/selfservice/fetchMarketConfig';
import { filterConfigsByClaims } from 'utils/selfservice/claimFilter';

// Constants for configurator field names to avoid hardcoded strings
const CONFIGURATOR_FIELDS = {
  DCC: 'selectionDCC',
  SCC: 'selectionSCC',
} as const;

// Dashboard-visible flags only. Excluded: brandSwitcher, localeSwitcher, sessionTimeout,
// scheduler, comparison, dgaCamFlight, carDetails (managed in market detail panel).
const editableFlagKeys: (keyof IFeatureFlags)[] = [
  '2D',
  'wltp',
  'selectionVin',
  'selectionCode',
  'selectionCarline',
  'selectionDCC',
  'selectionSCC',
  'sharingSameDevice',
  'sharingBroadcast',
  'sharingBroadcastLocal',
  'sharingRemote',
  'sharingDga',
];

// Grouped column keys that get a subtle background shade instead of borders.
const carInputKeys: Set<keyof IFeatureFlags> = new Set([
  'selectionVin',
  'selectionCode',
  'selectionCarline',
  'selectionDCC',
  'selectionSCC',
]);
const dgaKeys: Set<keyof IFeatureFlags> = new Set(['sharingDga']);

function normalizeId(id: string) {
  return id.replace(/[^a-z0-9]/gi, '');
}

function reportLoadFailure(what: string) {
  return (error: unknown) => {
    logger.error(`[SelfService] failed to load ${what}`, error);
    reportUntoastedApiFailure(error);
  };
}

export const ConfigurationTable = () => {
  const elementRef = React.useRef<HTMLDivElement>(null);
  const [rows, setRows] = React.useState<ITenantConfig[]>([]);
  const [form, setForm] = React.useState<'market' | 'tenant' | undefined>();
  const [formDefaults, setFormDefaults] = React.useState<{
    brand?: string;
    market?: string;
    tenant?: string;
    requireTenant?: boolean;
  }>({});
  const [selectedRow, setSelectedRow] = React.useState<string | null>(null);
  const [tenants, setTenants] = React.useState<ITenant[]>([]);
  const [pinnedIds, setPinnedIds] = React.useState<Set<string>>(new Set());
  const [recentlyToggledIds, setRecentlyToggledIds] = React.useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const [dialogState, setDialogState] = React.useState<{
    isOpen: boolean;
    configuratorType: 'DCC' | 'SCC';
    row: ITenantConfig | null;
  }>({
    isOpen: false,
    configuratorType: 'DCC',
    row: null,
  });
  if (!user.groups.includes('3DEXHIBITION_SELFSERVICE_ADMIN')) {
    throw new CustomerFacingError(EErrorCode.UNAUTHORIZED);
  }

  const navigate = useNavigate();

  React.useEffect(() => {
    sendApiRequest('/tenants')
      .then((data) => {
        // an aborted duplicate request resolves undefined; the in-flight twin delivers the data
        if (data === undefined) return;
        setTenants(data as ITenant[]);
      })
      .catch(reportLoadFailure('tenants'));

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleDialogClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  React.useEffect(() => {
    const hash = window.location.hash;
    const id = hash ? hash.substring(1) : null;
    if (id) {
      const selector = `td#row-${normalizeId(id)}`;
      const element = elementRef?.current?.querySelector(selector);
      setSelectedRow(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [rows, elementRef]);

  React.useEffect(() => {
    // Fetch data from the API
    sendApiRequest('/config?brand=*&market=*')
      .then((data) => {
        if (data === undefined) return;
        const configs = filterConfigsByClaims(data as ITenantConfig[], {
          brands: user.brands,
          markets: user.markets,
        });
        configs.sort((a, b) => a.id.localeCompare(b.id));
        setRows(configs);
      })
      .catch(reportLoadFailure('configs'));
  }, []);

  const clearRowSelection = () => {
    setSelectedRow(null);
    window.history.replaceState(null, '', window.location.pathname);
  };

  const handlePinToTop = (id: string) => {
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedQuery(value);
    }, 150);
  };

  React.useEffect(() => {
    if (!window.location.hash && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const sortedRows = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      const aPinned = pinnedIds.has(a.id) ? 0 : 1;
      const bPinned = pinnedIds.has(b.id) ? 0 : 1;
      if (aPinned !== bPinned) return aPinned - bPinned;
      return a.id.localeCompare(b.id);
    });
  }, [rows, pinnedIds]);

  const filteredRows = React.useMemo(() => {
    const filter = createMarketFilter(debouncedQuery);
    return sortedRows.filter((row) =>
      filter({
        brand: row.brand,
        market: row.market,
        tenant: row.tenant,
        tenantName: tenants.find((t) => t.id === row.tenant)?.name,
      })
    );
  }, [sortedRows, debouncedQuery, tenants]);

  const handleDialogClose = () => {
    setDialogState({
      isOpen: false,
      configuratorType: 'DCC',
      row: null,
    });
  };

  const handleGoToConfig = () => {
    if (dialogState.row) {
      const tenantParam = encodeTenantParam(dialogState.row.tenant);
      navigate(`/super-sheep/self-service/${dialogState.row.brand}/${dialogState.row.market}/${tenantParam}`);
      handleDialogClose();
    }
  };

  const handleCheckboxChange = (row: ITenantConfig, field: keyof IFeatureFlags, value?: boolean) => {
    if (!row) return;

    const newValue = value ?? !row.flags[field];

    // Check if this is a DCC or SCC checkbox being enabled
    if (newValue && (field === CONFIGURATOR_FIELDS.DCC || field === CONFIGURATOR_FIELDS.SCC)) {
      const configuratorType = field === CONFIGURATOR_FIELDS.DCC ? 'DCC' : 'SCC';
      const configuratorKey = field === CONFIGURATOR_FIELDS.DCC ? 'dcc' : 'scc';

      // Check if there are any configured links for this configurator type
      const hasLinks = row[configuratorKey] && row[configuratorKey].length > 0;
      if (!hasLinks) {
        // Show dialog to guide user to add links, but still enable the checkbox
        setDialogState({
          isOpen: true,
          configuratorType,
          row,
        });
        // Enable the checkbox even without links
        const newFlags = {
          ...row.flags,
          [field]: newValue,
        };
        saveConfig(row, newFlags);
        return;
      }
    }

    const newFlags = {
      ...row.flags,
      [field]: newValue,
    };

    saveConfig(row, newFlags);

    if (row.id !== selectedRow) {
      clearRowSelection();
    }
  };

  const saveConfig = async (row: ITenantConfig, flags?: IFeatureFlags) => {
    try {
      const updatedRow = { ...row, flags: flags || row.flags };

      // Optimistic update using functional updater to avoid stale closures
      setRows((prev) => prev.map((r) => (r.id === row.id ? updatedRow : r)));

      // API call
      await sendApiRequest(`/config/${row.id}`, {
        method: 'PUT',
        body: updatedRow,
      });
    } catch (error) {
      // Revert on error - restore original row state
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
      console.error('Failed to update:', error);
    }
  };

  // Enable or disable market
  const toggleMarket = async (row: ITenantConfig) => {
    try {
      if (!row) return;

      const newRow = { ...row, enabled: row.enabled === undefined ? false : !row.enabled };

      // Track recently toggled ID for pulse animation
      setRecentlyToggledIds((prev) => {
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });
      setTimeout(() => {
        setRecentlyToggledIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }, 1000);

      // Optimistic update using functional updater to avoid stale closures
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...newRow } : r)));

      // API call
      await sendApiRequest(`/config/${row.id}`, {
        method: 'PUT',
        body: { ...newRow },
      });
    } catch (error) {
      // Revert on error - restore original row state
      setRows((prev) => prev.map((r) => (r.id === row.id ? row : r)));
      console.error('Failed to update:', error);
    }

    if (row.id !== selectedRow) {
      clearRowSelection();
    }
  };

  return (
    <div className="min-h-full bg-gray-10050" ref={elementRef}>
      <div className="py-10">
        <header>
          <div className="mx-auto px-4 sm:flex sm:items-center sm:justify-between lg:px-6">
            <div>
              <h1 className="text-xl font-bold tracking-tight text-gray-900">Feature Overview</h1>
              <p className="mt-1 text-sm text-gray-500">
                Enable and disable 3D Exhibition features available in your region.
              </p>
            </div>
            <div className="mt-3 sm:ml-4 sm:mt-0">
              <button
                type="button"
                className="inline-flex items-center rounded-md bg-teal px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark"
                onClick={() => {
                  setFormDefaults({});
                  setForm('market');
                }}
              >
                New Market
              </button>
            </div>
          </div>
        </header>
        {form && (
          <Dialog onClose={() => setForm(undefined)} variant="centered">
            {form === 'market' && (
              <NewMarketForm
                onCancel={() => setForm(undefined)}
                onCreateTenant={() => setForm('tenant')}
                defaultBrand={formDefaults.brand}
                defaultMarket={formDefaults.market}
                defaultTenant={formDefaults.tenant}
                requireTenant={formDefaults.requireTenant}
                tenants={tenants}
              />
            )}
            {form === 'tenant' && (
              <NewTenantForm
                onCancel={() => setForm(undefined)}
                onBack={() => setForm('market')}
                onSave={(tenant: ITenant) => {
                  setTenants((prev) => [...prev, tenant]);
                  setFormDefaults((prev) => ({ ...prev, tenant: tenant.id }));
                  setForm('market');
                }}
              />
            )}
          </Dialog>
        )}
        <main>
          <div className="ring-gray-900/5 mx-auto bg-white shadow-sm ring-1 sm:rounded-lg">
            <div className="border-gray-900/5 border-b px-4 py-3">
              <div className="relative">
                <svg
                  className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
                  />
                </svg>
                <input
                  ref={searchInputRef}
                  type="text"
                  className="bg-transparent w-full rounded-md border-0 py-1.5 pl-10 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
                  placeholder="Search markets, brands, or tenants..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="hover:text-gray-600 absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                    onClick={() => {
                      handleSearchChange('');
                      searchInputRef.current?.focus();
                    }}
                    aria-label="Clear search"
                  >
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div className="relative max-h-[calc(100vh-220px)] overflow-auto">
              <table className="w-full table-auto">
                <colgroup>
                  <col className="w-[180px]" />
                  <col className="w-[120px]" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-16" />
                  <col className="w-10" />
                </colgroup>
                <thead className="sticky top-0 z-20">
                  {/* Row 1: Group headers */}
                  <tr>
                    <th
                      scope="col"
                      rowSpan={2}
                      className="sticky left-0 z-10 bg-white py-2.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900"
                    >
                      Market ID
                    </th>
                    <th
                      scope="col"
                      rowSpan={2}
                      className="sticky left-[180px] z-10 bg-white py-2.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900"
                    >
                      Tenant
                    </th>
                    <th
                      scope="col"
                      rowSpan={2}
                      className="bg-white py-2.5 text-center text-xs font-semibold text-gray-900"
                    >
                      2D
                    </th>
                    <th
                      scope="col"
                      rowSpan={2}
                      className="bg-white py-2.5 text-center text-xs font-semibold text-gray-900"
                    >
                      WLTP
                    </th>
                    <th
                      scope="col"
                      colSpan={5}
                      className="bg-gray-100 py-2 text-center text-xs font-semibold text-gray-500"
                    >
                      Car Input
                    </th>
                    <th
                      scope="col"
                      colSpan={4}
                      className="bg-white py-2 text-center text-xs font-semibold text-gray-500"
                    >
                      Sharing options
                    </th>
                    <th
                      scope="col"
                      rowSpan={2}
                      className="bg-gray-100 py-2.5 text-center text-xs font-semibold text-gray-900"
                    >
                      DGA
                    </th>
                    <th scope="col" rowSpan={2} className="w-10 bg-white py-2.5" />
                  </tr>
                  {/* Row 2: Sub-column headers under Car Input and Sharing options */}
                  <tr>
                    <th scope="col" className="bg-gray-100 py-2 text-center text-xs font-semibold text-gray-900">
                      VIN
                    </th>
                    <th scope="col" className="bg-gray-100 py-2 text-center text-xs font-semibold text-gray-900">
                      Code
                    </th>
                    <th scope="col" className="bg-gray-100 py-2 text-center text-xs font-semibold text-gray-900">
                      Carlines
                    </th>
                    <th scope="col" className="bg-gray-100 py-2 text-center text-xs font-semibold text-gray-900">
                      DCC
                    </th>
                    <th scope="col" className="bg-gray-100 py-2 text-center text-xs font-semibold text-gray-900">
                      SCC
                    </th>
                    <th scope="col" className="bg-white py-2 text-center text-xs font-semibold text-gray-900">
                      <span title="Same Device">Same Dev…</span>
                    </th>
                    <th scope="col" className="bg-white py-2 text-center text-xs font-semibold text-gray-900">
                      <span title="Broadcast to ChromeBox">ChromeBox</span>
                    </th>
                    <th scope="col" className="bg-white py-2 text-center text-xs font-semibold text-gray-900">
                      <span title="Broadcast to Local Renderer">Local Ren…</span>
                    </th>
                    <th scope="col" className="bg-white py-2 text-center text-xs font-semibold text-gray-900">
                      Remote
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {filteredRows.map((row) => (
                    <tr
                      key={row.id}
                      className={classNames(
                        'group transition-colors duration-150',
                        selectedRow === row.id ? 'bg-teal-600-10 hover:bg-teal-600-10' : 'hover:bg-gray-100'
                      )}
                    >
                      {/* Market ID with inline status dot */}
                      <td
                        className="sticky left-0 z-10 max-w-[180px] truncate bg-white py-2.5 pl-4 pr-3 text-xs font-medium text-gray-900 transition-colors duration-150 group-hover:bg-gray-100"
                        id={`row-${normalizeId(row.id)}`}
                        title={row.id}
                      >
                        <button
                          type="button"
                          data-testid="market-row-button"
                          className="flex items-center gap-2 text-left font-medium text-gray-900 hover:underline"
                          onClick={() => {
                            const tenantParam = encodeTenantParam(row.tenant);
                            navigate(`/super-sheep/self-service/${row.brand}/${row.market}/${tenantParam}`);
                          }}
                        >
                          <span
                            data-testid="status-dot"
                            className={classNames(
                              'size-2.5 shrink-0 rounded-full',
                              row.enabled !== false ? 'bg-teal' : 'bg-red-system-500',
                              recentlyToggledIds.has(row.id) ? 'animate-pulse' : ''
                            )}
                          />
                          {pinnedIds.has(row.id) && (
                            <svg className="size-4 shrink-0" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M9.59099 1.00216C9.78625 0.806897 10.1028 0.806897 10.2981 1.00216L15.2478 5.95191C15.4431 6.14717 15.4431 6.46375 15.2478 6.65901L13.8336 8.07323C13.6384 8.26849 13.3218 8.26849 13.1265 8.07323L12.773 7.71967L11.0052 9.48744L10.6813 11.1068C10.662 11.2036 10.6144 11.2925 10.5446 11.3623L9.23744 12.6694C9.04218 12.8647 8.72559 12.8647 8.53033 12.6694L3.58058 7.71967C3.38532 7.52441 3.38532 7.20783 3.58058 7.01257L4.88775 5.7054C4.95755 5.6356 5.04645 5.58802 5.14325 5.56866L6.76256 5.2448L8.53033 3.47703L8.17678 3.12348C7.98152 2.92822 7.98152 2.61164 8.17678 2.41637L9.59099 1.00216Z"
                                fill="#008075"
                              />
                              <path
                                d="M4.9948 10.5481L5.7019 11.2552L3.78261 13.1745L3.22703 13.023L3.07551 12.4674L4.9948 10.5481Z"
                                fill="#008075"
                              />
                            </svg>
                          )}
                          <img
                            data-testid="brand-logo"
                            src={brandLogos[row.brand] || brandLogos['*']}
                            alt={row.brand}
                            className="h-4 w-6 shrink-0 object-contain brightness-0"
                          />
                          {getMarketDisplayName(row.market)}
                        </button>
                      </td>
                      {/* Tenant */}
                      <td className="sticky left-[180px] z-10 whitespace-nowrap bg-white px-3 py-2.5 text-xs text-gray-700 transition-colors duration-150 group-hover:bg-gray-100">
                        {tenants.find((tenant) => tenant.id === row.tenant)?.name ?? 'All tenants'}
                      </td>
                      {/* Feature flag checkboxes */}
                      {editableFlagKeys.map((field) => (
                        <td
                          key={field}
                          className={classNames(
                            'whitespace-nowrap px-3 py-2.5 text-center text-xs',
                            carInputKeys.has(field) || dgaKeys.has(field) ? 'bg-gray-100' : ''
                          )}
                        >
                          <input
                            type="checkbox"
                            aria-label={`${field} for ${row.brand}-${row.market}`}
                            className="size-4 rounded border-gray-300 text-teal accent-teal focus:ring-teal"
                            checked={!!row.flags[field]}
                            onChange={() => handleCheckboxChange(row, field)}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-2.5 text-center">
                        <ContextMenu
                          isActive={row.enabled !== false}
                          isPinned={pinnedIds.has(row.id)}
                          onToggleActive={() => toggleMarket(row)}
                          onAddTenant={() => {
                            setFormDefaults({ brand: row.brand, market: row.market, requireTenant: true });
                            setForm('market');
                          }}
                          onPinToTop={() => handlePinToTop(row.id)}
                        />
                      </td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 && searchQuery && (
                    <tr>
                      <td colSpan={editableFlagKeys.length + 4} className="py-8 text-center text-sm text-gray-500">
                        No markets found for &ldquo;{searchQuery}&rdquo;
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>
      <ConfiguratorValidationDialog
        isOpen={dialogState.isOpen}
        onClose={handleDialogClose}
        onGoToConfig={handleGoToConfig}
        configuratorType={dialogState.configuratorType}
      />
    </div>
  );
};
